import type { Condition } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Grid, Icon, Stack, Tag, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
} from '@eluve/fhir-renderer';

import {
  type ProblemList,
  conditionToken,
  firstPendingCondition,
  icd10Code,
  pendingReviewCount,
} from '../problem-list';
import { isRefuted } from '../review-markers';

/**
 * The Overview-tab Conditions card (ENG-5365): the patient's problem list —
 * pending AI drafts and current problems first, past (resolved / inactive)
 * conditions below. Rows are rendered by the FHIR renderer (`ConditionView`
 * inList); all interactivity (row click targets, review pill) and the
 * ICD-10 / Draft badging are app chrome around renderer primitives, per the
 * renderer's presentational contract.
 */
export type ConditionsCardProps = {
  list: ProblemList;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a condition (routing owned by the section). */
  onOpenCondition: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="regular">
      {children}
    </Text>
  </Grid>
);

const PendingDot: React.FC = () => (
  <span
    aria-hidden
    className="size-1.75 rounded-full border-[1.5px] bg-bg-primary"
    style={{ borderColor: 'var(--color-text-insights)' }}
  />
);

const ConditionRows: React.FC<{
  conditions: Condition[];
  pending?: boolean;
  onOpenCondition: (token: string) => void;
}> = ({ conditions, pending = false, onOpenCondition }) => {
  const map = useMemo(() => buildResolutionMap(conditions), [conditions]);
  return (
    <FhirResourceScope map={map}>
      {conditions.map((condition) => {
        const icd10 = icd10Code(condition);
        return (
          <button
            key={conditionToken(condition)}
            type="button"
            onClick={() => onOpenCondition(conditionToken(condition))}
            data-testid="condition-row"
            className="flex w-full items-center gap-3 px-4 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
          >
            <div className="min-w-0 flex-1">
              <RenderResource resource={condition} inList />
            </div>
            {icd10 && <Tag pill subtle size="xs" color="gray" label={icd10} />}
            {pending && (
              <Tag
                pill
                subtle
                size="xs"
                color="insights"
                label={isRefuted(condition) ? 'Denial · Draft' : 'Draft'}
              />
            )}
            <Icon
              name="ChevronRight"
              size="xs"
              className="shrink-0 text-text-tertiary"
            />
          </button>
        );
      })}
    </FhirResourceScope>
  );
};

export const ConditionsCard: React.FC<ConditionsCardProps> = ({
  list,
  loading,
  failed,
  paging,
  onOpenCondition,
}) => {
  const pendingCount = pendingReviewCount(list);
  const firstPending = firstPendingCondition(list);
  const isEmpty =
    list.pending.length === 0 &&
    list.active.length === 0 &&
    list.past.length === 0;

  return (
    <section
      data-testid="conditions-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Conditions
        </Text>
        {pendingCount > 0 && firstPending && (
          <button
            type="button"
            onClick={() => onOpenCondition(conditionToken(firstPending))}
            data-testid="conditions-review-pill"
            className="flex items-center gap-1.5 rounded-full border border-border-primary px-2.5 py-0.5 text-xs font-semibold hover:bg-bg-secondary"
          >
            <PendingDot />
            {pendingCount} to review
          </button>
        )}
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load conditions.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading conditions…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No conditions recorded.</CenteredNote>
      ) : (
        <>
          {(list.pending.length > 0 || list.active.length > 0) && (
            <div className="border-t border-border-primary">
              <ConditionRows
                conditions={list.pending}
                pending
                onOpenCondition={onOpenCondition}
              />
              <ConditionRows
                conditions={list.active}
                onOpenCondition={onOpenCondition}
              />
            </div>
          )}
          {list.past.length > 0 && (
            <div className="border-t border-border-primary">
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="px-4 pt-3 pb-1 tracking-wide uppercase"
              >
                Past conditions
              </Text>
              <ConditionRows
                conditions={list.past}
                onOpenCondition={onOpenCondition}
              />
            </div>
          )}
          <LoadMoreRow paging={paging} noun="conditions" />
        </>
      )}
    </section>
  );
};
